import React,{useRef} from "react";
import { Box, Avatar, useTheme, IconButton, Menu, MenuItem } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import BaseCard from "../../../components/Cards/Card";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import { BASE_URL } from "../../../components/BaseUrl";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import AddIcon from "@mui/icons-material/Add"; // MUI plus icon
import { setProfileDraft, getProfileDraft } from "./ProfileDraft"; // 👈 profile draft utils
import CloseIcon from "@mui/icons-material/Close";
import Webcam from "react-webcam";
import { getApiMessage } from '../../../utils/messageHandler';
import document1 from "../../../assets/document1.svg";
import imageicon from "../../../assets/imageicon.png";
import {useState} from "react";
const CreateProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const profileUpdateToken =
    useSelector((s) => s.auth.profileUpdateToken) ||
    localStorage.getItem("profileUpdateToken");

  // Avatar state
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [avatarDataUrl, setAvatarDataUrl] = React.useState(""); // preview
const dobRef = useRef(null);
  // Document upload state
  const documentInputRef = React.useRef(null);
  const [uploadingDocument, setUploadingDocument] = React.useState(false);
  const [documentFileName, setDocumentFileName] = React.useState("");

  // Menu state for avatar options
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openOptions = Boolean(anchorEl);

  // Camera state
  const [showCamera, setShowCamera] = React.useState(false);
  const webcamRef = React.useRef(null);
  const [capturedImage, setCapturedImage] = React.useState(null);
  const [takingPhoto, setTakingPhoto] = React.useState(false);


  // Document menu (same pattern as avatar)
const [docAnchorEl, setDocAnchorEl] = useState(null);
const openDocMenu = Boolean(docAnchorEl);


// Camera for document
const [showDocCamera, setShowDocCamera] = useState(false);
const docWebcamRef = React.useRef(null);
const [docCapturedImage, setDocCapturedImage] = useState(null);
const [takingDocPhoto, setTakingDocPhoto] = useState(false);
  const handleOpenOptions = (e) => setAnchorEl(e.currentTarget);
  const handleCloseOptions = () => setAnchorEl(null);

  const handleSelectCamera = () => {
    setAnchorEl(null);
    openCamera();
  };

  const handleSelectGallery = () => {
    setAnchorEl(null);
    handlePickFile();
  };

  const handlePickFile = () => fileInputRef.current?.click();
  const handlePickDocument = () => documentInputRef.current?.click();

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const uploadImageToServer = async (file) => {
    const form = new FormData();
    form.append("image", file);

    const res = await fetch(`${BASE_URL}/upload/image`, {
      method: "POST",
      body: form,
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || "Upload failed");

    return data?.data?.url || data?.url || data?.path || "";
  };

  const uploadDocumentToServer = async (file) => {
    const form = new FormData();
    form.append("pdf", file);
    const res = await fetch(`${BASE_URL}/upload/pdf`, {
      method: "POST",
      body: form,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || "Upload failed");
    return data?.data?.url || data?.url || data?.path || "";
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const dataUrl = await fileToDataUrl(file);
      setAvatarDataUrl(dataUrl);
      const uploadedUrl = await uploadImageToServer(file); // 👈 upload to server
      formik.setFieldValue("avatar", uploadedUrl); // store server URL
    } catch (err) {
      const errorMsg = err.response?.data
        ? getApiMessage(err.response.data, t("createProfile.failedToUploadImage") || "Failed to upload image")
        : (err.message || t("createProfile.failedToUploadImage") || "Failed to upload image");
      toast.error(errorMsg);
      setAvatarDataUrl("");
      formik.setFieldValue("avatar", "");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
const [documentType, setDocumentType] = useState(null);

  const handleDocumentChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Automatically detect type
    const type = file.type.startsWith("image/")
      ? "image"
      : file.type === "application/pdf"
        ? "pdf"
        : null;
const fileName = file.name;
setDocumentFileName(fileName);
const isPDF = file.type === "application/pdf";
setDocumentType(isPDF ? "pdf" : "image");
    if (!type) {
      toast.error(t("createProfile.validFileRequired") || "Please select a valid image or PDF file");
      if (documentInputRef.current) documentInputRef.current.value = "";
      return;
    }

    try {
      setUploadingDocument(true);
      setDocumentFileName(file.name);

      // Build FormData
      const form = new FormData();
      form.append(type, file);

      // Choose correct endpoint
      const endpoint = type === "image" ? "/upload/image" : "/upload/pdf";

      const res = await fetch(`${BASE_URL}${endpoint}`, {
        method: "POST",
        body: form,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Upload failed");

      const uploadedUrl = data?.data?.url || data?.url || data?.path || "";

      // Save uploaded file URL to Formik
      formik.setFieldValue("document_url", uploadedUrl);

      // toast.success(t("createProfile.documentUploaded", { type: type.toUpperCase() }) || `${type.toUpperCase()} uploaded successfully`);
    } catch (err) {
      const errorMsg = err.response?.data
        ? getApiMessage(err.response.data, t("createProfile.uploadFailed") || "Upload failed")
        : (err.message || t("createProfile.uploadFailed") || "Upload failed");
      toast.error(errorMsg);
      formik.setFieldValue("document_url", "");
      setDocumentFileName("");
    } finally {
      setUploadingDocument(false);
      if (documentInputRef.current) documentInputRef.current.value = "";
    }
  };

  const openCamera = () => {
    setCapturedImage(null);
    setShowCamera(true);
  };

  const capturePhoto = async () => {
    if (!webcamRef.current) return;
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      toast.error(t("createProfile.failedToCaptureImage") || "Failed to capture image");
      return;
    }

    setCapturedImage(imageSrc);
    setTakingPhoto(true);

    try {
      // convert base64 → blob
      const blob = await fetch(imageSrc).then((res) => res.blob());
      const formData = new FormData();
      formData.append("image", blob, "avatar.jpg");

      const res = await fetch(`${BASE_URL}/upload/image`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data?.message || "Upload failed");

      const imageUrl = data?.data?.url || data?.url || data?.path || "";
      setAvatarDataUrl(imageSrc);
      formik.setFieldValue("avatar", imageUrl);
      toast.success(t("createProfile.photoCaptured") || "Photo captured & uploaded!");
      setShowCamera(false);
    } catch (err) {
      const errorMsg = err.response?.data
        ? getApiMessage(err.response.data, t("createProfile.failedToUploadPhoto") || "Failed to upload photo")
        : (err.message || t("createProfile.failedToUploadPhoto") || "Failed to upload photo");
      toast.error(errorMsg);
    } finally {
      setTakingPhoto(false);
    }
  };
  const showErr = (name) =>
    Boolean(formik.errors[name] && (formik.touched[name] || formik.submitCount > 0));

  const formik = useFormik({
    initialValues: {
      avatar: "",
      phone: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      dob: "",
      // document_url: "",
    },
    validate: (v) => {
      const e = {};
      if (!v.avatar) e.avatar = t("createProfile.avatarRequired") || "Profile image is required";

      // First name and last name validation - required fields
      if (!v.first_name?.trim()) e.first_name = t("createProfile.firstNameRequired") || "First name is required";
      if (!v.last_name?.trim()) e.last_name = t("createProfile.lastNameRequired") || "Last name is required";

      if (!v.phone) {
        e.phone = t("createProfile.phoneRequired") || "Phone is required";
      } else if (!/^\d+$/.test(v.phone)) {
        e.phone = t("createProfile.phoneDigitsOnly") || "Digits only";
      } else if (v.phone.length < 10 || v.phone.length > 15) {
        e.phone = t("createProfile.phoneInvalid") || "Invalid phone number";
      }

      if (!v.dob) {
        e.dob = t("createProfile.dobRequired") || "Date of birth is required";
      }

      // if (!v.document_url) {
      //   e.document_url = t("createProfile.documentRequired") || "Legal document is required";
      // }

      return e;
    },
    onSubmit: async (values) => {
      try {
        setSaving(true);

        // Save to profile draft instead of hitting API
        const draftUpdate = {
          first_name: values.first_name,
          middle_name: values.middle_name,
          last_name: values.last_name,
          phone: values.phone,
          profile_image: values.avatar,
          dob: values.dob,
          document_url: values.document_url,
        };

        const updated = setProfileDraft(draftUpdate);

        console.log("📦 Profile Draft saved (Step 1):", updated);
        // toast.success(t("createProfile.profileDataSaved") || "profile data saved");

        // Navigate to next step
        setTimeout(() => {
          navigate("/location");
        }, 400);
      } catch (err) {
        const errorMsg = err.response?.data
          ? getApiMessage(err.response.data, t("createProfile.failedToSaveDraft") || "Failed to save draft")
          : (err.message || t("createProfile.failedToSaveDraft") || "Failed to save draft");
        toast.error(errorMsg);
      } finally {
        setSaving(false);
      }
    },
  });


  const handleDobChange = (e) => {
  const value = e.target.value;
  const selectedDate = new Date(value);

  const today = new Date();
  const age =
    today.getFullYear() - selectedDate.getFullYear() -
    (today < new Date(today.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()) ? 1 : 0);

  if (age < 18) {
   toast.error(t("age"));
    return; // do not update formik
  }

  formik.setFieldValue("dob", value);
};


  return (
    <Box sx={{ py: isMobile ? 5 : 8, px: 2, mt: isMobile ? 2 : 0 }}>
      <Box sx={{ display: "flex", justifyContent: "center", minHeight: "100vh", alignItems: "center" }}>
        <BaseCard maxWidth={isMobile ? "100%" : isTablet ? "60%" : isLaptop ? "38%" : "38%"}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 25,
                  height: 25,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                1
              </Box>
              <Box sx={{ width: isMobile ? 50 : 95, height: 2, backgroundColor: "#091E4224" }} />
              <Box
                sx={{
                  width: 25,
                  height: 25,
                  borderRadius: "50%",
                  backgroundColor: "#ffffff",
                  color: "#BCBFC2",
                  border: "2px solid #091E4224",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                2
              </Box>
              <Box sx={{ width: isMobile ? 50 : 95, height: 2, backgroundColor: "#091E4224" }} />
              <Box
                sx={{
                  width: 25,
                  height: 25,
                  borderRadius: "50%",
                  backgroundColor: "#ffffff",
                  color: "#BCBFC2",
                  border: "2px solid  #091E4224",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                3
              </Box>
            </Box>
          </Box>

          <CustomText
            sx={{
              ...textStyles.h4,
              color: theme.palette.tertiary.main,
              textAlign: "left",
              fontSize: "26px",
            }}
          >
            {t("createProfileTitle")}
          </CustomText>
          <CustomText
            sx={{
              ...textStyles.body5,
              color: theme.palette.text.primary,
              pt: 0.7,
              textAlign: "left",
            }}
          >
            {t("createProfileSubtitle")}
          </CustomText>

          {/* Form */}
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ mb: 2, mt: 2 }}>
              <label style={{ color: "#626F86", fontSize: "15px" }}>
                {t("profilePicture")} <span style={{ color: "red" }}>*</span>
              </label>

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    border: `2px dashed ${showErr("avatar") ? "#d32f2f" : "#091E4224"}`,
                    display: "grid",
                    placeItems: "center",
                    cursor: uploading ? "not-allowed" : "pointer",
                  }}
                  onClick={(e) => {
                    if (!uploading) {
                      e.stopPropagation();
                      handleOpenOptions(e);
                    }
                  }}
                  title={uploading ? "Uploading..." : "Click to upload"}
                >
                  <Avatar src={avatarDataUrl || undefined} alt="" sx={{ width: 120, height: 120 }} />

                  <IconButton
                    size="small"
                    sx={{
                      fontSize: "14px",
                      position: "absolute",
                      top: 1,
                      right: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #ccc",
                      boxShadow: 1,
                      "&:hover": { backgroundColor: "#f0f0f0" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenOptions(e);
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>

                  {uploading && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        backdropFilter: "blur(1px)",
                        borderRadius: "50%",
                      }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  )}
                </Box>

                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={openOptions}
                onClose={handleCloseOptions}
              >
                <MenuItem onClick={handleSelectCamera} sx={{ fontSize: "13px", fontFamily: 'Poppins, sans-serif' }}>
                  {t("takePhoto")}
                </MenuItem>

                <MenuItem onClick={handleSelectGallery} sx={{ fontSize: "13px", fontFamily: 'Poppins, sans-serif' }}>
                  {t("chooseFromGallery")}
                </MenuItem>
              </Menu>
            </Box>

            {/* First Name, Middle Name, and Last Name Fields */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <label style={{ color: "#626F86", fontSize: 14, marginBottom: 7 }}>
                  {t("firstName")} <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  name="first_name"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="project_input"
                  style={{
                    height: "40px",
                    border: showErr("first_name") ? "1px solid #d32f2f" : "2px solid #091E4224",
                  }}
                />
                {formik.errors.first_name && (
                  <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.first_name}</Box>
                )}
              </Box>

              <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <label style={{ color: "#626F86", fontSize: 14, marginBottom: 7 }}>
                  {t("middleName")}
                </label>
                <input
                  name="middle_name"
                  value={formik.values.middle_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="project_input"
                  style={{
                    height: "40px",
                    border: showErr("middle_name") ? "1px solid #d32f2f" : "2px solid #091E4224",
                  }}
                />
                {formik.errors.middle_name && (
                  <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.middle_name}</Box>
                )}
              </Box>

              <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <label style={{ color: "#626F86", fontSize: 14, marginBottom: 7 }}>
                  {t("lastName")} <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  name="last_name"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="project_input"
                  style={{
                    height: "40px",
                    border: showErr("last_name") ? "1px solid #d32f2f" : "2px solid #091E4224",
                  }}
                />
                {formik.errors.last_name && (
                  <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.last_name}</Box>
                )}
              </Box>
            </Box>

            {/* Date of Birth */}
            <Box sx={{ mb: 2, display: "flex", flexDirection: "column" }}>
              <label style={{ color: "#626F86", fontSize: 14, marginBottom: 7 }}>
                {t("settings.dateOfBirth")}<span style={{ color: "red" }}>*</span>
              </label>
              <input
                  ref={dobRef}
    onClick={() => dobRef.current?.showPicker()} 
                type="date"
                name="dob"
                value={formik.values.dob}
                // onChange={formik.handleChange}
                onChange={(e) => handleDobChange(e)}
                className="project_input"
                style={{
                  height: "40px",
                  border: showErr("dob") ? "1px solid #d32f2f" : "2px solid #091E4224",
                }}
              />
              {formik.errors.dob && (
                <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.dob}</Box>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <label
                style={{
                  color: "#626F86",
                  fontSize: 14,
                  marginBottom: 7,
                  display: "inline-block",
                }}
              >
                {t("phoneNumber")} <span style={{ color: "red" }}>*</span>
              </label>
              <Box
                sx={{
                  border: showErr("phone") ? "1px solid #d32f2f" : "2px solid #091E4224",


                  borderRadius: "6px",
                  "& .form-control": {
                    border: "none !important",
                    outline: "none !important",
                    boxShadow: "none !important",
                    width: "100%",
                    height: "40px",
                    fontSize: "14px",
                    paddingLeft: "48px",
                    borderRadius: "6px",
                    fontFamily: "Poppins, sans-serif !important",
                  },
                  "& .flag-dropdown": {
                    border: "none !important",
                    borderRight: "1px solid #ccc !important",
                    backgroundColor: "#f9f9f9",
                    borderTopLeftRadius: "6px",
                    borderBottomLeftRadius: "6px",
                  },
                }}
              >
                <PhoneInput
                  country={"us"}
                  countryCodeEditable={false}
                  value={formik.values.phone}
                  onChange={(value) => formik.setFieldValue("phone", value)}
                  inputProps={{ name: "phone", required: true }}
                  containerClass="react-tel-input"
                  inputClass="form-control"
                  buttonClass="flag-dropdown"
                />
              </Box>
              {formik.errors.phone && (
                <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.phone}</Box>
              )}
            </Box>

       

            <Box sx={{ mb: 2 }}>
              <label
                style={{
                  color: "#626F86",
                  fontSize: 14,
                  marginBottom: 7,
                  display: "inline-block",
                }}
              >
                {t("settings.nationalId")}
              </label>

              <Box
                sx={{
                  position: "relative",
                  border: `2px dashed ${showErr("document_url") ? "#d32f2f" : "#091E4224"}`,
                  borderRadius: "6px",
                  padding: "20px",
                  textAlign: "center",
                  cursor: uploadingDocument ? "not-allowed" : "pointer",
                  backgroundColor: "#f9f9f9",
                  "&:hover": {
                    backgroundColor: uploadingDocument ? "#f9f9f9" : "#f0f0f0",
                  },
                }}
             onClick={(e) => {
if (!uploadingDocument) {
e.stopPropagation();
setDocAnchorEl(e.currentTarget);
}
}}
              >
                {uploadingDocument ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    <CircularProgress size={20} />
                    <span style={{ fontSize: 14, color: "#666" }}>{t("settings.uploading")}</span>
                  </Box>
                ) : documentFileName ? (
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    {/* File name */}
                      <Box sx={{ mb: 0 }}>
    {documentType === "pdf" ? (
      <img src={document1} alt="PDF" style={{ width: 30, height: 30 }} />
    ) : (
      <img src={imageicon} alt="Image" style={{ width: 30, height: 30}} />
    )}
  </Box>

                    <span style={{ fontSize: 14, color: "#16a34a" }}>✓ {documentFileName}</span>
                    <br />
                    <span style={{ fontSize: 12, color: "#666" }}>
                      {t("settings.clickToChangeDocument")}
                    </span>

                    {/* Cross button */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDocumentFileName(null);
                        formik.setFieldValue("document_url", "");
                      }}
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        backgroundColor: "white",
                        boxShadow: "0 0 4px rgba(0,0,0,0.2)",
                        "&:hover": { backgroundColor: "#f0f0f0" },
                      }}
                    >
                      <CloseIcon sx={{ color: "red", fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box>
                    <span style={{ fontSize: 14, color: "#666" }}>
                      {t("settings.dragFileHereOr")}{" "}
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        color: "#006EC2",
                        textDecoration: "underline",
                      }}
                    >
                      {t("settings.clickToBrowse")}
                    </span>
                    <br />
                    {/* <span style={{ fontSize: 12, color: "#999" }}>{t("settings.pdfOnly")}</span> */}
                  </Box>
                )}
              </Box>
<span style={{ fontSize: 12, color: "#999" }}>{t("validation")}</span>
              <input
                ref={documentInputRef}
                type="file"
                accept="image/*,.pdf,application/pdf"
                hidden
                onChange={handleDocumentChange}
              />

              {formik.errors.document_url && (
                <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>
                  {formik.errors.document_url}
                </Box>
              )}
            </Box>

<Menu
  anchorEl={docAnchorEl}
  open={openDocMenu}
  onClose={() => setDocAnchorEl(null)}
>
  <MenuItem
    onClick={() => {
      setDocAnchorEl(null);
      setDocCapturedImage(null);
      setShowDocCamera(true);
    }}
     sx={{ fontSize: "13px", fontFamily: 'Poppins, sans-serif' }}
  >
    {t("takePhoto")}
  </MenuItem>

  <MenuItem
    onClick={() => {
      setDocAnchorEl(null);
      handlePickDocument(); // 👈 existing input (image/pdf)
    }}
     sx={{ fontSize: "13px", fontFamily: 'Poppins, sans-serif' }}
  >
    {t("chooseFromGallery")}
  </MenuItem>
</Menu>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 4 }}>

              <CustomButton
                label={
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    {saving && <CircularProgress size={18} sx={{ color: "#fff" }} />}
                    {t("next")}
                  </Box>
                }
                type="submit"
                disabled={saving}
                sx={{ px: 6 }}
              />
            </Box>
          </form>
        </BaseCard>
      </Box>

      {showCamera && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1300,
            p: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              maxWidth: 550,
            }}
          >
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
              {!capturedImage ? (
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{ facingMode: "user", width: 550, height: 500 }}
                  style={{
                    borderRadius: "12px",
                    width: "100%",
                    maxWidth: "550px",
                    objectFit: "contain",
                    border: "2px solid #E0E0E0",
                  }}
                />
              ) : (
                <img
                  src={capturedImage}
                  alt="Captured"
                  style={{
                    borderRadius: "12px",
                    width: "100%",
                    maxWidth: "550px",
                    border: "2px solid #E0E0E0",
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 3,
                width: "100%",
              }}
            >
              {!capturedImage ? (
                <CustomButton
                  label={takingPhoto ? t("uploading") : t("capture")}
                  onClick={capturePhoto}
                  disabled={takingPhoto}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <CustomButton
                  label={t("retake")}
                  onClick={() => setCapturedImage(null)}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    borderRadius: "8px",
                  }}
                />
              )}
              <CustomButton
                label={t("close")}
                onClick={() => setShowCamera(false)}
                sx={{
                  flex: 1,
                  py: 1.2,
                  borderRadius: "8px",
                  backgroundColor: "#E0E0E0",
                  color: "#333",
                  "&:hover": { backgroundColor: "#D5D5D5" },
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
      {showDocCamera && (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1300,
      p: 2,
    }}
  >
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        padding: "24px",
        width: "100%",
        maxWidth: 550,
      }}
    >
      {!docCapturedImage ? (
        <Webcam
          ref={docWebcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={{ facingMode: "environment" }}
          style={{ width: "100%", borderRadius: 12 }}
        />
      ) : (
        <img
          src={docCapturedImage}
          alt="Captured document"
          style={{ width: "100%", borderRadius: 12 }}
        />
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        {!docCapturedImage ? (
          <CustomButton
            label={takingDocPhoto ? t("uploading") : t("capture")}
            disabled={takingDocPhoto}
            onClick={async () => {
              const img = docWebcamRef.current?.getScreenshot();
              if (!img) return;

              setDocCapturedImage(img);
              setTakingDocPhoto(true);

              try {
                const blob = await fetch(img).then(r => r.blob());
                const form = new FormData();
                form.append("image", blob, "document.jpg");

                const res = await fetch(`${BASE_URL}/upload/image`, {
                  method: "POST",
                  body: form,
                });
                const data = await res.json();
                if (!res.ok) throw new Error("Upload failed");

                const url = data?.data?.url || data?.url;
                formik.setFieldValue("document_url", url);
                setDocumentFileName("document.jpg");
                setDocumentType("image");

                toast.success(t("createProfile.documentUploaded"));
                setShowDocCamera(false);
              } catch {
                toast.error(t("createProfile.uploadFailed"));
              } finally {
                setTakingDocPhoto(false);
              }
            }}
          />
        ) : (
          <CustomButton
            label={t("retake")}
            onClick={() => setDocCapturedImage(null)}
          />
        )}

        <CustomButton
          label={t("close")}
          onClick={() => setShowDocCamera(false)}
        />
      </Box>
    </Box>
  </Box>
)}
    </Box>
  );
};

export default CreateProfile;













